import * as React from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import {
  HistoryIcon,
  RunningStatusIcon,
  SettingsIcon,
  TextFileIcon,
} from '../../SVG';
import CloseIcon from '@mui/icons-material/Close';
import { Badge, Box, Grid, ListItem, SvgIcon } from '@mui/material';
import { SparkScrollableGridContainer } from 'sharedComponents/UI';
import MemoryEditHistory from './MemoryEditHistory';
import MemoryEditFiles from './MemoryEditFiles';
import MemoryEditSettings from './MemoryEditSettings';
import MemoryStatuses from './MemoryStatuses';
import { useMemoryDiagnostics } from 'hooks/memory/useMemoryDiagnostics';

type MenuButtonProps = {
  icon: React.ReactNode;
  isActive: boolean;
  badgeCount?: number;
  warning?: boolean;
};

const MenuButton = ({
  icon,
  isActive,
  badgeCount,
  warning,
}: MenuButtonProps) => {
  const getButtonStyles = (theme: Theme) => {
    if (isActive) {
      return {
        backgroundColor: theme.palette.primary.main,
        stroke: theme.palette.common.white,
        border: `1px solid ${theme.palette.primary.main}`,
      };
    }
    if (warning) {
      return {
        backgroundColor: theme.palette.warning.light,
        stroke: theme.palette.warning.main,
        border: `1px solid ${theme.palette.warning.main}`,
      };
    }

    return {
      backgroundColor: theme.palette.common.white,
      stroke: theme.palette.primary.main,
      border: `1px solid ${theme.palette.common.white}`,
    };
  };

  return (
    <ListItem
      disablePadding
      sx={{
        display: 'block',
      }}
    >
      <Box
        sx={(theme) => ({
          position: 'relative',
          margin: '0.5rem',
          borderRadius: '9px',
          display: 'flex',
          aspectRatio: 1,
          height: '3rem',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: getButtonStyles(theme).backgroundColor,
          border: getButtonStyles(theme).border,
        })}
      >
        <SvgIcon
          sx={(theme) => ({
            stroke: getButtonStyles(theme).stroke,
          })}
        >
          {icon}
        </SvgIcon>
        {warning && badgeCount && (
          <Badge
            badgeContent={badgeCount}
            color='warning'
            sx={{
              position: 'absolute',
              top: 3,
              right: 4,
              '& .MuiBadge-colorWarning': {
                color: 'white',
              },
            }}
          />
        )}
      </Box>
    </ListItem>
  );
};

const primaryListItem = [
  {
    id: 1,
    tag: 'memoryStatus',
    header: 'Status',
    icon: <RunningStatusIcon />,
  },
  {
    id: 2,
    tag: 'builderStatus',
    header: 'Settings',
    icon: <SettingsIcon />,
  },
  {
    id: 3,
    tag: 'settings',
    header: 'Files',
    icon: <TextFileIcon />,
  },
  {
    id: 4,
    tag: 'sparks',
    header: 'Chat History',
    icon: <HistoryIcon />,
  },
];

const openedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  width: 700,
  overflowX: 'hidden',
  boxShadow: theme.shadows[2],
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: '4.35rem',
  overflowX: 'hidden',
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  '& .MuiPaper-root': {
    border: 'none',
    top: '6rem',
    height: 'calc(100vh - 6rem)',
  },
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

type MemoryEditSideNavProps = {
  memoryID: string;
  handleSideNavItemClick: (sideNavItemIndex: number) => void;
  closeSideNav: () => void;
  selectedNavListItemID: number | null;
  setSelectedNavListItemID: (id: number) => void;
  onMemoryChatClick: (memoryChatID: string) => void;
  activeMemoryChatID: string;
};

function MemoryEditSideNav({
  memoryID,
  handleSideNavItemClick,
  selectedNavListItemID,
  closeSideNav,
  setSelectedNavListItemID,
  onMemoryChatClick,
  activeMemoryChatID,
}: MemoryEditSideNavProps) {
  const { isLoading: isMemoryDiagnosticsLoading, data: memoryDiagnostics } =
    useMemoryDiagnostics(memoryID);
  const renderContent = () => {
    switch (selectedNavListItemID) {
      case 1:
        return (
          <MemoryStatuses
            issues={memoryDiagnostics?.data?.meta?.diagnostics?.issues}
            isMemoryDiagnosticsLoading={isMemoryDiagnosticsLoading}
            setSelectedNavListItemID={setSelectedNavListItemID}
          />
        );
      case 2:
        return (
          <MemoryEditSettings memoryID={memoryID} closeSideNav={closeSideNav} />
        );

      case 3:
        return <MemoryEditFiles memoryID={memoryID} />;

      case 4:
        return (
          <MemoryEditHistory
            memoryID={memoryID}
            onMemoryChatClick={onMemoryChatClick}
            activeMemoryChatID={activeMemoryChatID}
          />
        );
      default:
        return <></>;
    }
  };

  const open = !!selectedNavListItemID;
  return (
    <Drawer
      variant='permanent'
      open={open}
      // sx={{
      //   '& .MuiDrawer-paper': {
      //     borderRight: (theme) => `1px solid ${theme.palette.borderColor}`,
      //     maxWidth: '50%',
      //   },
      // }}
    >
      <Grid
        container
        flexWrap='nowrap'
        overflow='hidden'
        height='100%'
        border={(theme) => `1px solid ${theme.palette.borderColor}`}
        borderTop='none'
      >
        <Grid
          item
          sx={{
            borderRight: (theme) =>
              open ? `1px solid ${theme.palette.borderColor}` : 'none',
          }}
        >
          {primaryListItem.map(({ id, icon }) => (
            <span
              onClick={() => handleSideNavItemClick(id)}
              key={id}
              style={{ cursor: 'pointer' }}
            >
              <MenuButton
                key={id}
                icon={icon}
                isActive={id === selectedNavListItemID}
                warning={
                  memoryDiagnostics?.data?.meta?.diagnostics?.hasIssues &&
                  id === 1
                }
                badgeCount={
                  memoryDiagnostics?.data?.meta?.diagnostics?.totalIssues
                }
              />
            </span>
          ))}
        </Grid>

        {!!selectedNavListItemID && (
          <SparkScrollableGridContainer
            item
            header={
              primaryListItem.find((item) => item.id === selectedNavListItemID)
                ?.header || ''
            }
            scrollableContainerStyle={{
              flexDirection: 'column',
              alignContent: 'stretch',
            }}
          >
            {renderContent()}
          </SparkScrollableGridContainer>
        )}
      </Grid>
      {open && (
        <IconButton
          aria-label='close'
          onClick={closeSideNav}
          sx={{
            color: (theme) => theme.palette.primary.main,
            position: 'absolute',
            top: '2rem',
            right: 0,
            transform: 'translate(-50%,-50%)',
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Drawer>
  );
}

export default MemoryEditSideNav;
